import { Box, Typography } from "@mui/material";

import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as Share03Icon } from "components/Icons/share-03.svg";
import { InfoBox } from "components/Layout/InfoBox";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { LinkItem } from "LinkItem";
import React from "react";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    container: {
      backgroundColor: theme.gray50,
      borderRadius: "8px",
      padding: theme.spacing(12),
      "&:not(:last-child)": {
        marginBottom: theme.spacing(12),
      },
    },
    title: {
      fontWeight: 500,
      marginBottom: theme.spacing(12),
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(4),
    },
    description: {
      fontSize: 14,
      whiteSpace: "break-spaces",
      "& p": {
        fontSize: 14,
        whiteSpace: "break-spaces",
      },
      "& img": {
        width: "100%",
      },
    },
    linksContainer: {
      display: "flex",
      marginTop: theme.spacing(12),
    },
    link: {
      display: "flex",
      alignItems: "center",
      marginRight: theme.spacing(12),
      "& > svg": {
        marginLeft: theme.spacing(2),
        width: "14px",
        height: "14px",
      },
    },
    bolded: {
      fontWeight: "bold",
    },
    infoBox: {
      marginTop: theme.spacing(12),
    },
  }),
);

interface Props {
  title: string;
  titleAdditionalInfo?: React.ReactElement;
  description?: string | React.ReactElement;
  links?: {
    label: string;
    path: string;
    icon?: React.ReactElement;
  }[];
  infoBoxText?: string;
  warnBoxText?: string;
}

export const HelpPanel = ({
  links,
  title,
  titleAdditionalInfo,
  description,
  infoBoxText,
  warnBoxText,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>
        {message({ id: title })}
        {titleAdditionalInfo && titleAdditionalInfo}
      </Typography>
      {!!description &&
        (typeof description === "string" ? (
          <Typography className={classes.description}>
            {message(
              {
                id: description,
              },
              {
                span: (chunks: React.ReactNode[]) => (
                  <span className={classes.bolded}>{chunks}</span>
                ),
              },
            )}
          </Typography>
        ) : (
          <div className={classes.description}>{description}</div>
        ))}
      {!!infoBoxText && (
        <InfoBox
          type="info"
          text={message({ id: infoBoxText })}
          className={classes.infoBox}
        />
      )}
      {!!warnBoxText && (
        <InfoBox
          type="warning"
          text={message(
            { id: warnBoxText },
            {
              span: (chunks: React.ReactNode[]) => (
                <span className={classes.bolded}>{chunks}</span>
              ),
            },
          )}
          className={classes.infoBox}
        />
      )}
      {links && (
        <div className={classes.linksContainer}>
          {links.map((link, index) => (
            <LinkItem
              key={index}
              blueSmall
              to={link.path}
              className={classes.link}
              target="_blank"
            >
              {message({ id: link.label })} {link.icon || <Share03Icon />}
            </LinkItem>
          ))}
        </div>
      )}
    </Box>
  );
};
